// Shared benchmark-percentile helpers for the three redesign candidates.
//
// The real data layer (riskScore.js BENCHMARK) exposes only three scalar
// anchors - top25 (31), platformAvg (48), industryAvg (54) - and NO true
// per-peer distribution or percentile column (the Postgres contract is
// industry_benchmarks(industry_id, metric_id, value)). So we derive an
// APPROXIMATE percentile by monotonic piecewise-linear interpolation across
// those anchors plus the band endpoints, round to the nearest 5, and every
// surface that shows it labels it "approx". This is strictly more honest
// than the old synthetic 16-bin bell curve: it is anchored to real values
// and discloses that it is an estimate. When the backend later exposes a
// real org_percentile + cohort_size, percentileOf() is replaced by a direct
// read and the "approx" affordance disappears with no layout change.
//
// "Safer than X%" = the share of peer orgs with a HIGHER (worse) risk score
// than you. Lower score = safer, so a low score maps to a high percentile.
import { ORG_SCORE, BENCHMARK } from '../../riskScore';

// Hardcoded prototype value, matching the current card. Shown only as a
// cohort-size context line ("142 peer orgs"), never as a false ordinal rank
// ("102nd of 142") until a real cohort size is wired.
export function peerCount() { return BENCHMARK.cohortSize || PEER_COUNT_FALLBACK; }
const PEER_COUNT_FALLBACK = 0;
export const PEER_COUNT = PEER_COUNT_FALLBACK; // deprecated: use peerCount()

const TOP25 = () => BENCHMARK.top25;             // 75th percentile (top-quartile gate)
const PLATFORM_AVG = () => BENCHMARK.platformAvg; // 50th percentile (platform cohort)
const INDUSTRY_AVG = () => BENCHMARK.industryAvg; // 50th percentile (industry cohort)

// Build the score -> percentile anchor set for a cohort. The cohort average
// is the 50th-percentile (median proxy); the top-quartile threshold and the
// 0/100 band endpoints pin the rest of the monotonic curve.
function anchorsFor(cohortAvg) {
    return [
        { s: 0, p: 100 },
        { s: TOP25() ?? 31, p: 75 },
        { s: cohortAvg, p: 50 },
        { s: 100, p: 0 },
    ].sort((a, b) => a.s - b.s);
}

// Approximate "safer than" percentile (0-100, rounded to nearest 5) for a
// score within a cohort. Monotonic decreasing in score.
export function percentileOf(score, cohortAvg) {
    if (score == null) return null;
    const anchors = anchorsFor(cohortAvg);
    const x = Math.max(0, Math.min(100, score));
    let p = anchors[anchors.length - 1].p;
    if (x <= anchors[0].s) {
        p = anchors[0].p;
    } else {
        for (let i = 0; i < anchors.length - 1; i++) {
            const a = anchors[i];
            const b = anchors[i + 1];
            if (x >= a.s && x <= b.s) {
                const t = b.s === a.s ? 0 : (x - a.s) / (b.s - a.s);
                p = a.p + t * (b.p - a.p);
                break;
            }
        }
    }
    return Math.max(0, Math.min(100, Math.round(p / 5) * 5));
}

// Resolve the cohort the org is benchmarked against. Licensed tenants rank
// against their industry peer group; everyone else against the whole
// platform.
export function cohortFor(industryOn) {
    return industryOn
        ? { avg: INDUSTRY_AVG(), label: BENCHMARK.industry || 'industry', noun: `${BENCHMARK.industry || 'industry'} peers`, sub: `${BENCHMARK.industry || 'Industry'} · ${peerCount()} peer orgs · refreshed monthly` }
        : { avg: PLATFORM_AVG(), label: 'platform', noun: 'all orgs on the platform', sub: `Platform-wide · refreshed monthly` };
}

// Plain-English verdict from a percentile. >=50 reads as "safer than"; the
// bottom tail flips to "higher risk than" so it never reads as an error.
export function verdictFor(pct, cohortNoun) {
    if (pct == null) return { lead: 'Benchmark', value: '', tail: '', adverse: false };
    if (pct >= 50) {
        return { lead: 'Safer than', value: `~${pct}%`, tail: `of ${cohortNoun}`, adverse: false };
    }
    return { lead: 'Higher risk than', value: `~${100 - pct}%`, tail: `of ${cohortNoun}`, adverse: true };
}

// -- Since-last-visit, read once at module load -----------------------------
// Users do not check daily, so movement anchors against the last time this
// viewer looked. We use our OWN keys (not the dial card's) and read/advance
// exactly once at import so the three candidate cards stay consistent with
// each other and never race the dial card's localStorage write.
const LAST_KEY = 'rbx_bench_lastvisit_score';
const LAST_AT_KEY = 'rbx_bench_lastvisit_at';

// Resolved lazily on first use (not at module import) so it reads the live,
// hydrated ORG_SCORE rather than the null the model holds before hydration.
// Memoised so the read/advance still happens exactly once per session.
let _lastVisit = null;
function resolveLastVisit() {
    if (_lastVisit) return _lastVisit;
    let prevScore = null;
    let prevAt = null;
    try {
        const stored = localStorage.getItem(LAST_KEY);
        const storedAt = localStorage.getItem(LAST_AT_KEY);
        if (stored !== null) {
            prevScore = parseInt(stored, 10);
            prevAt = storedAt;
        }
        if (ORG_SCORE != null) {
            localStorage.setItem(LAST_KEY, String(ORG_SCORE));
            localStorage.setItem(LAST_AT_KEY, new Date().toISOString());
        }
    } catch {
        /* localStorage disabled */
    }
    _lastVisit = { prevScore, prevAt };
    return _lastVisit;
}

export function lastVisitLabel(iso) {
    if (!iso) return 'first visit';
    const d = new Date(iso);
    const diffDays = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
}

// Movement in percentile points since the last visit, for a cohort. Positive
// = moved up (safer). Returns null when there is no prior visit.
export function movementSinceLastVisit(currentScore, cohortAvg) {
    const lastVisit = resolveLastVisit();
    if (lastVisit.prevScore == null || currentScore == null) {
        return { deltaPct: null, label: lastVisitLabel(lastVisit.prevAt) };
    }
    const now = percentileOf(currentScore, cohortAvg);
    const prev = percentileOf(lastVisit.prevScore, cohortAvg);
    return { deltaPct: now - prev, label: lastVisitLabel(lastVisit.prevAt) };
}
